import * as React from 'react'
import { graphql, useStaticQuery, Link } from 'gatsby'
import { Row, Col, Container } from 'react-bootstrap'
import extractLinkData from '@utility/extractLinkData'
import { UserType, SitemapItem, Sitemap } from '@type/component-data-types.d'

type Props = {
  userType: UserType
}

// リンクの見出し部分のsubコンポーネント
const NavTitle = {
  Partner: function Partner() {
    return (
      <>
        <div className="globalFooter__partner">
          <Link to="/">パートナー会員向けトップ</Link>
        </div>
      </>
    )
  },
  Client: function Client() {
    return (
      <>
        <div className="globalFooter__client">
          <Link to="/advertiser/" data-cta-location="footer">
            広告主会員向けトップ
          </Link>
        </div>
      </>
    )
  },
  Sitemap: function Sitemap() {
    return (
      <>
        <div className="globalFooter__sitemap">
          <Link to="/sitemap/">サイトマップ</Link>
        </div>
      </>
    )
  },
}

// memo: LinkAPIを使用した時にページ上部にスクロールしないページが存在します.(AFBTOP-194)
// この為、暫定処置として、footerのリンクは強制的にスクロールさせます。これはその為の関数です。
const provisionalLinkScroll = () => {
  setTimeout(() => {
    window.scroll(0, 1)
  }, 50)
}

const NavList = (navItem: Array<SitemapItem>): React.JSX.Element => {
  return (
    <>
      <ul className="globalFooter__item">
        {navItem.map((elm: SitemapItem, index: number) => (
          <li className="globalFooter__itemList" key={`nabItem${index}`}>
            {elm.useLinkApi ? (
              <Link
                to={elm.path}
                className="globalFooter__link"
                onClick={provisionalLinkScroll}
              >
                {elm.name}
              </Link>
            ) : (
              <a href={elm.path} className="globalFooter__link">
                {elm.name}
              </a>
            )}
          </li>
        ))}
      </ul>
    </>
  )
}

// markup
const GlobalFooter = React.memo((props: Props): React.JSX.Element => {
  const isUserType = props.userType || 'both'

  // GQL query
  const data = useStaticQuery(graphql`
    query {
      allDataJson {
        nodes {
          partnerLinkData {
            path
            name
            key
            useLinkApi
          }
          clientLinkData {
            key
            name
            path
            useLinkApi
          }
          corporateLinkData {
            key
            name
            path
            useLinkApi
          }
        }
      }
    }
  `)
  const linkData: any = data.allDataJson.nodes[0]

  // memo: ページごとに表示内容が異なる為、パートナー向けのリンク先を取得する為のkeyを取得する
  const sitemapPartner: Array<SitemapItem> = extractLinkData(
    linkData.partnerLinkData,
    [
      'aboutAffiliate',
      'aboutAfb',
      'ppcAffiliateAlias',
      'adAffiliateAlias',
      'selfb',
      'snsAffiliate',
      'instagramAffiliate',
      'tiktokAffiliateAlias',
      'entry',
      'snsEntry',
      'legalTipsAlias',
      'clean',
      'illegal',
      'seminar',
      'feature',
      'faq',
      'guide',
      'contact',
    ],
  )
  // memo: ページごとに表示内容が異なる為、クライアント向けのリンク先を取得する為のkeyを取得する
  const sitemapClient: Array<SitemapItem> = extractLinkData(
    linkData.clientLinkData,
    [
      'clientAboutAfb',
      'clientIntroduction',
      'clientFaq',
      'clientInterview',
      'clientContact',
      'clientDocumentDownload',
    ],
  )
  // memo: コーポレート向けのリンク先を取得する
  const sitemapCorporate: Array<SitemapItem> = extractLinkData(
    linkData.corporateLinkData,
    ['company', 'privacyPolicy', 'contact'],
  )

  const contents: Sitemap = {
    corporate: sitemapCorporate,
    partner: sitemapPartner,
    client: sitemapClient,
  }

  return (
    <div className="globalFooter">
      <footer className="globalFooter__body">
        <Container fluid="lg">
          <Row className="globalFooter__main gap-1">
            <Col className="globalFooter__navList">
              {/* クライアントページかパートナーページを条件にコンテンツを出し分ける */}
              {(() => {
                if (isUserType === 'client') {
                  return (
                    <>
                      <NavTitle.Client />
                      {NavList(contents.client)}
                    </>
                  )
                } else {
                  return (
                    <>
                      <NavTitle.Partner />
                      {NavList(contents.partner)}
                    </>
                  )
                }
              })()}
            </Col>
            <Col className="globalFooter__navList">
              {(() => {
                if (isUserType === 'partner') {
                  return <NavTitle.Client />
                } else if (isUserType === 'client') {
                  return <NavTitle.Partner />
                } else {
                  return (
                    <>
                      <NavTitle.Client />
                      {NavList(contents.client)}
                    </>
                  )
                }
              })()}
              <NavTitle.Sitemap />
            </Col>
          </Row>
        </Container>
        <div className="globalFooter__corporate">
          <Container fluid="lg">
            <div className="globalFooter__corporateItem d-md-flex">
              <div className="d-mb-inline-block">
                <ul className="globalFooter__corporateLinks">
                  {contents.corporate?.map((elm: any, index: number) => (
                    <li className="d-inline-block" key={`corporate${index}`}>
                      <Link to={elm.path} className="globalFooter__link">
                        {elm.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="globalFooter__copyright d-mb-inline-block">
                Copyright For it Inc. All Rights Reserved.
              </div>
            </div>
          </Container>
        </div>
      </footer>
    </div>
  )
})

export default GlobalFooter
